/**
 * TrameLivretCross.jsx — Bandeau cross-navigation Trame ↔ Livret.
 *
 * À placer en haut de chaque trame pédagogique (Plateau, 125, Passerelle)
 * et de chaque livret élève. Rend visible en 1 tap le passage :
 *   - depuis la trame → vers le livret interactif correspondant
 *   - depuis le livret → vers la trame pédagogique correspondante
 *
 * ⚠️ La carte « Trame pédagogique » (contenu enseignant) est MASQUÉE
 * pour les visiteurs sans compte / élèves. Elle n'apparaît qu'aux
 * utilisateurs identifiés comme enseignant/admin ou dans l'audience
 * enseignant / futur-enseignant.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpenCheck, ClipboardList, ArrowRight, Sparkles } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import useAudience, { AUDIENCES } from '../../hooks/useAudience';

export default function TrameLivretCross({
  current = 'trame',
  trameTo, trameLabel = 'Trame pédagogique',
  livretTo, livretLabel = 'Livret interactif à cocher',
  hubTo = '/formations-2-roues',
}) {
  const isTrame = current === 'trame';
  const { user } = useApp();
  const { audience } = useAudience();

  const isTeacherRole = !!(user && (user.role === 'enseignant' || user.role === 'admin' || user.is_admin));
  const isTeacherAudience = audience === AUDIENCES.ENSEIGNANT || audience === AUDIENCES.FUTUR_ENSEIGNANT;
  const canSeeTrame = isTrame /* on est déjà sur la trame → forcément visible */
    || isTeacherRole
    || isTeacherAudience;

  return (
    <section className="no-print bg-[#0a0a0b] border-b border-white/5" data-testid="trame-livret-cross">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-3">
          {/* Trame — visible seulement pour teacher/audience enseignant OU si on est déjà dessus */}
          {canSeeTrame && (
            <Link
              to={trameTo}
              className={`flex-1 rounded-xl border p-3 flex items-center gap-3 transition ${
                isTrame
                  ? 'bg-[#141416] border-white/20 pointer-events-none'
                  : 'bg-[#141416] border-white/10 hover:border-rose-500/50 active:scale-[0.98]'
              }`}
              data-testid="cross-trame"
            >
              <span className="w-9 h-9 rounded-lg bg-rose-500/15 border border-rose-500/30 flex items-center justify-center flex-shrink-0">
                <BookOpenCheck className="w-4 h-4 text-rose-300" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-black uppercase tracking-widest text-rose-300 leading-none mb-0.5">
                  {isTrame ? '● Tu es sur la trame' : 'Trame pédagogique'}
                </div>
                <div className="text-white font-black text-[13px] leading-tight truncate">{trameLabel}</div>
              </div>
              {!isTrame && <ArrowRight className="w-4 h-4 text-white/40 flex-shrink-0" />}
            </Link>
          )}

          {/* Livret */}
          <Link
            to={livretTo}
            className={`flex-1 rounded-xl border p-3 flex items-center gap-3 transition ${
              !isTrame
                ? 'bg-[#141416] border-white/20 pointer-events-none'
                : 'bg-emerald-500/5 border-emerald-500/30 hover:border-emerald-500/60 active:scale-[0.98]'
            }`}
            data-testid="cross-livret"
          >
            <span className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-4 h-4 text-emerald-300" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[9px] font-black uppercase tracking-widest text-emerald-300 leading-none mb-0.5">
                {!isTrame ? '● Tu es sur le livret' : 'Livret élève interactif'}
              </div>
              <div className="text-white font-black text-[13px] leading-tight truncate">{livretLabel}</div>
            </div>
            {isTrame && <ArrowRight className="w-4 h-4 text-emerald-300 flex-shrink-0" />}
          </Link>

          {/* Hub */}
          <Link
            to={hubTo}
            className="rounded-xl border border-white/10 bg-white/5 hover:border-white/25 active:scale-[0.98] transition px-3 py-3 flex items-center gap-2 justify-center sm:justify-start"
            data-testid="cross-hub"
          >
            <Sparkles className="w-4 h-4 text-white/70" />
            <span className="text-white/80 text-[12px] font-black uppercase tracking-widest whitespace-nowrap">Toutes les trames</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
